import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import {
  parseReadableJsonString,
  toReadableJsonString,
} from '@aibees/elephant/feature/search-filters/readable-search-json';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';

type ScopeParams = Partial<SearchBrowseParams>;

export type ApplyResult = { ok: true } | { ok: false; errors: string[] };

function isScope(s: string | undefined): s is SearchTableScope {
  return s === 'company' || s === 'people' || s === 'employees';
}

/** Snapshot the current scope's params imperatively (no subscription — the
 *  header must not re-render on every keystroke). Mirrors use-share-search-link. */
function readScopeParams(scope: SearchTableScope): ScopeParams {
  const state = (
    useSearchParamsStore as unknown as {
      __store: {
        getState: () => {
          paramsByScope: Record<SearchTableScope, ScopeParams>;
        };
      };
    }
  ).__store.getState();
  return objSanitizeNilValues(state.paramsByScope[scope] ?? {}) as ScopeParams;
}

/**
 * Builds the current search as a readable JSON payload (`json`) and a
 * sample/template of every active filter key (`sampleJson`), and applies an
 * edited/pasted payload back into the store. Apply reuses the saved/shared-search
 * path (`replaceParamsForScope` + history reset), so filters, URL and results all
 * update to match.
 */
export function useSearchPayload() {
  const scope = useSearchTableScope();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();

  const { json, currentParams } = useMemo(() => {
    if (!isScope(scope))
      return { json: '{}', currentParams: {} as ScopeParams };
    const params = readScopeParams(scope);
    return {
      json: toReadableJsonString(scope, params),
      currentParams: params,
    };
  }, [scope]);

  /** True when the (valid) payload would produce the current active filters — so
   *  Apply is a no-op and should be disabled. Invalid JSON counts as "changed". */
  const matchesCurrent = useCallback(
    (text: string): boolean => {
      if (!isScope(scope)) return false;
      const res = parseReadableJsonString(scope, text);
      return res.ok && isEqual(res.params, currentParams);
    },
    [scope, currentParams]
  );

  const apply = useCallback(
    (text: string): ApplyResult => {
      if (!isScope(scope))
        return { ok: false, errors: ['Unsupported search scope.'] };
      const res = parseReadableJsonString(scope, text);
      if (!res.ok) return { ok: false, errors: res.errors };
      replaceParamsForScope(scope, res.params);
      resetHistoryTo(
        scope,
        objSanitizeNilValues(res.params) as Record<string, string>
      );
      return { ok: true };
    },
    [scope, replaceParamsForScope, resetHistoryTo]
  );

  return { json, apply, matchesCurrent };
}
